import { memo, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FormContainer,
  FormInput,
  TagsInput,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import {
  TWhatsappProfileForm,
  whatsappProfileSchema,
} from "./WhatsappProfileForm.schema";
import TicketServService from "../../../services/ticket.service";
import { IWhatsappProfile } from "../../../models/whatsapp-profile";
import { VerticalOptions } from "./VerticalOptions/VerticalOptions";
import { verticalOptions } from "./VerticalOptions/VerticalOptionValues";
import { ProfilePicture } from "./ProfilePicture/ProfilePicture";

const translationPath =
  "chatBot.page.register.ticketServiceWhats.whatsappProfilePath.";
const ticketService = new TicketServService();

export const WhatsappProfileForm = memo(({ onClose }: any) => {
  const { ticketServiceId } = useParams();
  const { t } = useTranslation();

  const [data, setData] = useState<IWhatsappProfile>();

  const { setMessage } = useNotificationContext();

  const { sendRequest: update, loading: uLoading } = useFetch(
    ticketService.updateWhatsappBusinesProfile
  );

  const {
    sendRequest: getWhatsappBusinesProfile,
    loading: loadingGetWhatsappBusinesProfile,
  } = useFetch(ticketService.getWhatsappBusinesProfile);

  const updateHandler = (v: TWhatsappProfileForm, id: string) => {
    return update({
      ticketServiceId: id,
      data: {
        ...v,
        vertical: v.vertical?.id,
      },
      file: v.file,
    });
  };

  const getHandler = useCallback(
    async (ticketServiceId: string): Promise<Treturn<TWhatsappProfileForm>> => {
      const { data, success } = await getWhatsappBusinesProfile({
        ticketServiceId,
      });
      if (data) {
        setData(data);
        const newData: TWhatsappProfileForm = {
          ...data,
          about: data.about,
          address: data.address,
          description: data.description,
          email: data.email,
          messagingProduct: data.messagingProduct,
          profilePictureUrl: data.profilePictureUrl,
          vertical: verticalOptions.find((a) => a.id == data?.vertical),
          websites: data.websites ? data.websites : [],
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [getWhatsappBusinesProfile]
  );

  const successHandle = (data: { success: boolean }) => {
    if (data && data.success) {
      setMessage({
        message: t(translationPath + "profileUpdated"),
      });
    } else {
      setMessage({
        message: t(translationPath + "error.profileUpdated"),
        type: "error",
      });
    }
    onClose();
  };

  const loading = loadingGetWhatsappBusinesProfile || uLoading;

  const defaultValue = useMemo(() => {
    const value = verticalOptions.find((a) => a.id == data?.vertical);
    return value;
  }, [verticalOptions]);

  return (
    <FormContainer<TWhatsappProfileForm>
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "whatsappProfileModalTitle")}
      subtitle={""}
      backButtonOnClick={onClose}
      schema={whatsappProfileSchema}
      showBackButton={false}
      onSuccess={successHandle}
      redirectOnSave={false}
      isModalForm={true}
      propId={ticketServiceId}
      marginBottom={0}
      customHeight={"fit-content"}
      formActionsOnBottom={false}
      defaultValues={{
        vertical: { id: defaultValue?.id, name: defaultValue?.name },
      }}
      fields={(type) => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="about"
              label={t(translationPath + "about") || "about"}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="description"
              label={t(translationPath + "description") || "description"}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="email"
              label={t(translationPath + "email") || "email"}
              size="small"
              fullWidth
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="address"
              label={t(translationPath + "address") || "address"}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TagsInput
              name="websites"
              size="small"
              label={t(translationPath + "websites") || "websites"}
              fullWidth
              helperText={t(translationPath + "websites_help_text")}
            />
          </Grid>
          <VerticalOptions />
          <ProfilePicture
            profilePictureUrl={data ? data.profilePictureUrl : null}
          />
        </Grid>
      )}
    />
  );
});
